import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ujmsReferrals, ujmsReferralDecision, ujmsArrests, ujmsRecordArrest,
  ujmsSubmitReferral, ujmsSearchCases,
} from "../../services/ujmsApi";
import { getOffenseCatalog } from "../../services/api";
import { Panel, Badge, thCls, tdCls, inputCls, selectCls, Field, btnPrimary, btnGhost, btnDanger, fmtDT, cls } from "../ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Siren, FileSignature } from "lucide-react";

const REF_FILTERS = ["PENDING_REVIEW", "CHARGED", "DECLINED", "RETURNED", "ALL"];

export default function CriminalPipeline() {
  const [tab, setTab] = useState("Referrals");
  return (
    <div className="space-y-4" data-testid="criminal-pipeline">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Criminal Pipeline</h1>
        <p className="text-sm text-slate-500">Law-enforcement referral → prosecutorial review → charging instrument → court case. Original referrals are never overwritten.</p>
      </div>
      <div className="border-b border-slate-200 flex gap-1">
        {["Referrals", "Arrests"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`pipeline-tab-${t.toLowerCase()}`}
            className={cls("px-3.5 py-2 text-sm border-b-2", tab === t ? "border-blue-900 text-blue-900 font-semibold" : "border-transparent text-slate-500 hover:text-slate-800")}>
            {t}
          </button>
        ))}
      </div>
      {tab === "Referrals" ? <ReferralsTab /> : <ArrestsTab />}
    </div>
  );
}

function ReferralsTab() {
  const [filter, setFilter] = useState("PENDING_REVIEW");
  const [rows, setRows] = useState([]);
  const [review, setReview] = useState(null);
  const [notes, setNotes] = useState("");
  const [caseQuery, setCaseQuery] = useState("");
  const [caseOptions, setCaseOptions] = useState([]);
  const [caseId, setCaseId] = useState("");

  const load = useCallback(() => {
    ujmsReferrals(filter === "ALL" ? {} : { status: filter }).then((r) => setRows(r.data)).catch(() => {});
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const searchCase = async () => {
    if (!caseQuery.trim()) return;
    const r = await ujmsSearchCases({ q: caseQuery, case_type: "CRIMINAL", page_size: 10 });
    setCaseOptions(r.data.items);
  };

  const decide = async (action) => {
    try {
      const payload = { action, notes };
      if (action === "CHARGE") payload.case_id = caseId;
      const r = await ujmsReferralDecision(review.id, payload);
      toast.success(action === "CHARGE" ? `CHARGE_AUTHORIZED — ${r.data.charges.length} count(s)` : `Referral ${action.toLowerCase()}d`);
      setReview(null); setNotes(""); setCaseId(""); setCaseOptions([]); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Decision failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-1">
        {REF_FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`ref-filter-${f.toLowerCase()}`}
            className={cls("text-xs font-semibold uppercase tracking-wide px-3 py-1.5 border rounded-sm",
              filter === f ? "bg-blue-900 text-white border-blue-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100")}>
            {f.replace("_", " ")}
          </button>
        ))}
      </div>
      <Panel testId="referrals-panel">
        <table className="w-full border-collapse">
          <thead><tr>
            <th className={thCls}>Received</th><th className={thCls}>Person</th><th className={thCls}>Agency</th>
            <th className={thCls}>Proposed Charges</th><th className={thCls}>Status</th><th className={thCls}>Case</th><th className={thCls}></th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`referral-row-${r.id}`}>
                <td className={`${tdCls} font-mono text-xs whitespace-nowrap`}>{fmtDT(r.created_at)}</td>
                <td className={tdCls}>{r.person_name}</td>
                <td className={`${tdCls} text-xs`}>{r.referring_agency}</td>
                <td className={tdCls}>
                  {(r.proposed_charges || []).map((c, i) => (
                    <div key={`${c.statute_code}-${i}`} className="text-xs"><span className="font-mono">{c.statute_code}</span> {c.description} <span className="text-slate-400">({c.classification})</span></div>
                  ))}
                </td>
                <td className={tdCls}><Badge value={r.status} /></td>
                <td className={`${tdCls} font-mono text-xs`}>{r.case_id ? <Link className="text-blue-900 hover:underline" to={`/ujms/cases/${r.case_id}`}>open case</Link> : "—"}</td>
                <td className={tdCls}>{r.status === "PENDING_REVIEW" && <button className={btnPrimary} onClick={() => setReview(r)} data-testid={`referral-review-${r.id}`}><FileSignature className="w-4 h-4" /> Review</button>}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={7} className={tdCls}>No referrals in this view.</td></tr>}
          </tbody>
        </table>
      </Panel>

      <Dialog open={!!review} onOpenChange={(v) => !v && setReview(null)}>
        <DialogContent className="max-w-2xl" data-testid="referral-decision-dialog">
          <DialogHeader><DialogTitle>Charging Decision — {review?.person_name}</DialogTitle></DialogHeader>
          {review && (
            <div className="space-y-3 text-sm">
              <div className="border border-slate-200 bg-slate-50 rounded-sm p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Original referral (preserved verbatim)</div>
                {(review.proposed_charges || []).map((c, i) => (
                  <div key={`${c.statute_code}-${i}`} className="text-sm"><span className="font-mono text-xs">{c.statute_code}</span> {c.description} <span className="text-slate-400 text-xs">({c.classification})</span></div>
                ))}
                {review.notes && <p className="text-xs text-slate-500 mt-1">{review.notes}</p>}
              </div>
              <Field label="Target case (required to authorize charges — open via Clerk Intake if none exists)">
                <div className="flex gap-2">
                  <input className={inputCls} placeholder="Search criminal cases (defendant, number)…" value={caseQuery} onChange={(e) => setCaseQuery(e.target.value)} data-testid="decision-case-search" />
                  <button className={btnGhost} onClick={searchCase} data-testid="decision-case-search-btn">Find</button>
                </div>
                {caseOptions.length > 0 && (
                  <select className={`${selectCls} mt-2`} value={caseId} onChange={(e) => setCaseId(e.target.value)} data-testid="decision-case-select">
                    <option value="">Select case…</option>
                    {caseOptions.map((c) => <option key={c.id} value={c.id}>{c.case_number} — {c.caption}</option>)}
                  </select>
                )}
              </Field>
              <Field label="Decision notes"><textarea className={inputCls} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="decision-notes" /></Field>
            </div>
          )}
          <DialogFooter>
            <button className={btnGhost} onClick={() => decide("RETURN")} data-testid="decision-return-btn">Return for Info</button>
            <button className={btnDanger} onClick={() => decide("DECLINE")} data-testid="decision-decline-btn">Decline</button>
            <button className={btnPrimary} disabled={!caseId} onClick={() => decide("CHARGE")} data-testid="decision-charge-btn">Authorize Charges</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ArrestsTab() {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [referring, setReferring] = useState(null);
  const [a, setA] = useState({ first_name: "", last_name: "", dob: "", arrest_date: new Date().toISOString().slice(0, 10), arresting_agency: "Chicago Police Department", location: "", narrative: "", booking_number: "" });
  const [charges, setCharges] = useState([{ statute_code: "", description: "", classification: "" }]);
  const [catalog, setCatalog] = useState({ offenses: [], categories: [] });

  useEffect(() => { getOffenseCatalog().then((r) => setCatalog(r.data)).catch(() => {}); }, []);

  const load = useCallback(() => { ujmsArrests().then((r) => setRows(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const record = async () => {
    try {
      await ujmsRecordArrest(a);
      toast.success("ARREST_RECORDED");
      setCreating(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const refer = async () => {
    try {
      await ujmsSubmitReferral({
        arrest_id: referring.id, person_id: referring.person_id,
        referring_agency: referring.arresting_agency,
        proposed_charges: charges.filter((c) => c.statute_code && c.description),
      });
      toast.success("REFERRAL_SUBMITTED — routed to prosecutorial review");
      setReferring(null); setCharges([{ statute_code: "", description: "", classification: "" }]);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button className={btnPrimary} onClick={() => setCreating(true)} data-testid="arrest-create-btn"><Siren className="w-4 h-4" /> Record Arrest</button>
      </div>
      <Panel testId="arrests-panel">
        <table className="w-full border-collapse">
          <thead><tr><th className={thCls}>Date</th><th className={thCls}>Person</th><th className={thCls}>Agency</th><th className={thCls}>Booking #</th><th className={thCls}>Narrative</th><th className={thCls}></th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50">
                <td className={`${tdCls} font-mono text-xs`}>{r.arrest_date}</td>
                <td className={tdCls}>{r.person_name}</td>
                <td className={`${tdCls} text-xs`}>{r.arresting_agency}</td>
                <td className={`${tdCls} font-mono text-xs`}>{r.booking_number || "—"}</td>
                <td className={tdCls}><span className="line-clamp-1 max-w-[260px] text-xs">{r.narrative}</span></td>
                <td className={tdCls}><button className={btnGhost} onClick={() => setReferring(r)} data-testid={`arrest-refer-${r.id}`}>Submit Referral</button></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={6} className={tdCls}>No arrests recorded.</td></tr>}
          </tbody>
        </table>
      </Panel>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent data-testid="arrest-dialog">
          <DialogHeader><DialogTitle>Record Arrest (fictional development data)</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First Name" required><input className={inputCls} value={a.first_name} onChange={(e) => setA({ ...a, first_name: e.target.value })} data-testid="arrest-first" /></Field>
            <Field label="Last Name" required><input className={inputCls} value={a.last_name} onChange={(e) => setA({ ...a, last_name: e.target.value })} data-testid="arrest-last" /></Field>
            <Field label="DOB"><input type="date" className={inputCls} value={a.dob} onChange={(e) => setA({ ...a, dob: e.target.value })} /></Field>
            <Field label="Arrest Date"><input type="date" className={inputCls} value={a.arrest_date} onChange={(e) => setA({ ...a, arrest_date: e.target.value })} /></Field>
            <Field label="Agency"><input className={inputCls} value={a.arresting_agency} onChange={(e) => setA({ ...a, arresting_agency: e.target.value })} /></Field>
            <Field label="Booking #"><input className={inputCls} value={a.booking_number} onChange={(e) => setA({ ...a, booking_number: e.target.value })} /></Field>
          </div>
          <Field label="Narrative"><textarea className={inputCls} rows={2} value={a.narrative} onChange={(e) => setA({ ...a, narrative: e.target.value })} /></Field>
          <DialogFooter>
            <button className={btnGhost} onClick={() => setCreating(false)}>Cancel</button>
            <button className={btnPrimary} disabled={!a.first_name || !a.last_name} onClick={record} data-testid="arrest-save-btn">Record</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!referring} onOpenChange={(v) => !v && setReferring(null)}>
        <DialogContent className="max-w-2xl" data-testid="refer-dialog">
          <DialogHeader><DialogTitle>Charge Referral — {referring?.person_name}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {charges.map((c, i) => (
              <div key={`${c.statute_code || "new"}-${i}`} className="space-y-1.5 border border-slate-200 rounded-sm p-2 bg-slate-50/50">
                <select className={selectCls} value=""
                  onChange={(e) => {
                    const o = catalog.offenses[Number(e.target.value)];
                    if (o) setCharges(charges.map((x, j) => j === i ? { statute_code: o.code, description: o.desc, classification: o.severity } : x));
                  }}
                  aria-label={`Pick count ${i + 1} from the Illinois offense catalog`} data-testid={`refer-catalog-${i}`}>
                  <option value="">Pick from the Illinois offense catalog — criminal, DUI/traffic, weapons… ({catalog.offenses.length} offenses)</option>
                  {catalog.categories.map((cat) => (
                    <optgroup key={cat} label={cat}>
                      {catalog.offenses.map((o, idx) => o.category === cat ? (
                        <option key={idx} value={idx}>{`${o.code} — ${o.desc} (${o.severity})`}</option>
                      ) : null)}
                    </optgroup>
                  ))}
                </select>
                <div className="grid grid-cols-3 gap-2">
                  <input className={inputCls} placeholder="Statute (720 ILCS …)" value={c.statute_code} onChange={(e) => setCharges(charges.map((x, j) => j === i ? { ...x, statute_code: e.target.value } : x))} data-testid={`refer-statute-${i}`} />
                  <input className={inputCls} placeholder="Description" value={c.description} onChange={(e) => setCharges(charges.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} data-testid={`refer-desc-${i}`} />
                  <input className={inputCls} placeholder="Classification" value={c.classification} onChange={(e) => setCharges(charges.map((x, j) => j === i ? { ...x, classification: e.target.value } : x))} data-testid={`refer-class-${i}`} />
                </div>
              </div>
            ))}
            <button className={btnGhost} onClick={() => setCharges([...charges, { statute_code: "", description: "", classification: "" }])} data-testid="refer-add-count">+ Add Count</button>
          </div>
          <DialogFooter>
            <button className={btnGhost} onClick={() => setReferring(null)}>Cancel</button>
            <button className={btnPrimary} disabled={!charges.some((c) => c.statute_code && c.description)} onClick={refer} data-testid="refer-save-btn">Submit for Review</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
